/**
 * QRCodeMock
 *
 * Renderiza un QR placeholder (grid de cuadrados) — NO es un QR real
 * escaneable. Solo para que visualmente parezca uno mientras estamos en
 * modo demo. En producción reemplazar por una librería tipo `qrcode.react`
 * que tome el `payload` real y dibuje el QR canónico.
 *
 * El seed pseudo-aleatorio depende del payload — así dos QR distintos
 * dibujan patterns distintos pero estables entre renders.
 */

interface QRCodeMockProps {
    payload: string;
    size?: number;
    /** Cantidad de celdas por lado del grid */
    grid?: number;
    className?: string;
}

/** Hash determinístico simple (string → array de bits) */
function hashToBits(s: string, count: number): boolean[] {
    const bits: boolean[] = [];
    let seed = 0;
    for (let i = 0; i < s.length; i++) {
        seed = ((seed << 5) - seed + s.charCodeAt(i)) | 0;
    }
    let x = Math.abs(seed) || 1;
    for (let i = 0; i < count; i++) {
        x = (x * 1664525 + 1013904223) & 0xffffffff;
        bits.push((x & 1) === 1);
    }
    return bits;
}

export default function QRCodeMock({
    payload,
    size = 200,
    grid = 25,
    className = "",
}: QRCodeMockProps) {
    const cellCount = grid * grid;
    const bits = hashToBits(payload, cellCount);
    const cellSize = size / grid;

    // Tres marcas de posición (esquinas) — patrón fijo del QR estándar
    function isFinderPattern(row: number, col: number): boolean {
        const inTopLeft = row < 7 && col < 7;
        const inTopRight = row < 7 && col >= grid - 7;
        const inBottomLeft = row >= grid - 7 && col < 7;
        if (!inTopLeft && !inTopRight && !inBottomLeft) return false;

        // Coordenadas relativas al marco 7x7
        const r = row < 7 ? row : grid - 1 - row;
        const c = col < 7 ? col : grid - 1 - col;
        // Borde 7x7
        if (r === 0 || r === 6) return true;
        if (c === 0 || c === 6) return true;
        // Cuadrado interior 3x3
        if (r >= 2 && r <= 4 && c >= 2 && c <= 4) return true;
        return false;
    }

    function isInsideFinder(row: number, col: number): boolean {
        // Toda la zona 7x7 — para vaciar el ruido de fondo
        if (row < 7 && col < 7) return true;
        if (row < 7 && col >= grid - 7) return true;
        if (row >= grid - 7 && col < 7) return true;
        return false;
    }

    return (
        <div
            className={`relative bg-white p-3 rounded-xl border border-slate-200 shadow-sm ${className}`}
            style={{ width: size + 24, height: size + 24 }}
        >
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                xmlns="http://www.w3.org/2000/svg"
                shapeRendering="crispEdges"
            >
                {Array.from({ length: grid }).map((_, row) =>
                    Array.from({ length: grid }).map((__, col) => {
                        const idx = row * grid + col;
                        let fill = false;
                        if (isInsideFinder(row, col)) {
                            fill = isFinderPattern(row, col);
                        } else {
                            fill = bits[idx];
                        }
                        return (
                            <rect
                                key={`${row}-${col}`}
                                x={col * cellSize}
                                y={row * cellSize}
                                width={cellSize}
                                height={cellSize}
                                fill={fill ? "#0F172A" : "transparent"}
                            />
                        );
                    }),
                )}
            </svg>
            {/* Logo de marca centrado */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-9 h-9 rounded-md bg-white border-2 border-slate-900 flex items-center justify-center shadow-sm">
                    <span className="text-[10px] font-black tracking-tighter text-slate-900">
                        DX
                    </span>
                </div>
            </div>
        </div>
    );
}
